// CLASSIC LOOK (v1.70): pre-Floodlight copy from ea44aec, used by
// screens/classic/CommunityScreen.jsx. Keep behaviour in step with
// components/community/SharedBetCard.jsx (the Floodlight version).
/**
 * SharedBetCard — a bet posted into a Community channel. Same surface language
 * as the result cards (indigo → violet → green edge on the left, tabular odds),
 * with a one-tap "Run this search" that re-runs the original query so readers
 * see live prices, not the poster's stale ones.
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import PressableScale from '../../motion/PressableScale';
import { formatAmerican } from '../../../utils/communityShare';

export default function SharedBetCard({ bet, onRun, compact }) {
    if (!bet) return null;
    const ev = Number(bet.evPercent);
    const hasEv = Number.isFinite(ev);
    return (
        <View style={[s.card, compact && { maxWidth: '100%' }]} accessible accessibilityLabel={`Shared bet: ${bet.title || bet.selection}${bet.odds != null ? `, ${formatAmerican(bet.odds)}` : ''}${bet.book ? ` at ${bet.book}` : ''}`}>
            <LinearGradient colors={['#6366F1', '#8B5CF6', '#34D399']} start={{ x: 0, y: 0 }} end={{ x: 0, y: 1 }} style={s.edge} />
            <View style={{ flex: 1, gap: 6 }}>
                <View style={s.top}>
                    <View style={[s.kind, bet.kind === 'sgp' && s.kindSgp]}>
                        <Text style={[s.kindText, bet.kind === 'sgp' && { color: '#C4B5FD' }]}>{bet.kind === 'sgp' ? 'SGP' : 'BET'}</Text>
                    </View>
                    <Text style={s.title} numberOfLines={2}>{bet.title || bet.selection}</Text>
                    {bet.odds != null && <Text style={s.odds}>{formatAmerican(bet.odds)}</Text>}
                </View>
                {!!bet.legs?.length && (
                    <View style={{ gap: 3 }}>
                        {bet.legs.map((l, i) => (
                            <View key={`${l.label}${i}`} style={s.leg}>
                                <View style={s.legDot} />
                                <Text style={s.legText} numberOfLines={1}>{l.label}</Text>
                                {l.odds != null && <Text style={s.legOdds}>{formatAmerican(l.odds)}</Text>}
                            </View>
                        ))}
                    </View>
                )}
                <View style={s.meta}>
                    {!!bet.book && <Text style={s.metaText}>{bet.book}</Text>}
                    {hasEv && (
                        <View style={[s.ev, ev < 0 && s.evNeg]}>
                            <Ionicons name={ev >= 0 ? 'trending-up' : 'trending-down'} size={11} color={ev >= 0 ? '#34D399' : '#F87171'} />
                            <Text style={[s.evText, ev < 0 && { color: '#F87171' }]}>{ev > 0 ? '+' : ''}{ev.toFixed(1)}% EV</Text>
                        </View>
                    )}
                    {!!bet.query && onRun && (
                        <PressableScale onPress={() => onRun(bet.query)} style={s.run} hoverStyle={{ backgroundColor: 'rgba(52,211,153,0.16)' }} accessibilityRole="button" accessibilityLabel={`Run the search ${bet.query}`}>
                            <Ionicons name="flash-outline" size={12} color="#34D399" />
                            <Text style={s.runText}>Today's price</Text>
                        </PressableScale>
                    )}
                </View>
            </View>
        </View>
    );
}

const s = StyleSheet.create({
    card: { flexDirection: 'row', gap: 12, marginTop: 8, padding: 12, paddingLeft: 0, borderRadius: 14, maxWidth: 520, backgroundColor: 'rgba(22,22,34,0.95)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
    edge: { width: 3, alignSelf: 'stretch', borderTopRightRadius: 3, borderBottomRightRadius: 3 },
    top: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    kind: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.08)' },
    kindSgp: { backgroundColor: 'rgba(139,92,246,0.18)' },
    kindText: { color: 'rgba(255,255,255,0.7)', fontSize: 9, fontWeight: '800', letterSpacing: 1 },
    title: { flex: 1, color: 'rgba(255,255,255,0.94)', fontSize: 14, fontWeight: '700' },
    odds: { color: '#fff', fontSize: 16, fontWeight: '800', fontVariant: ['tabular-nums'] },
    leg: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    legDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: 'rgba(139,92,246,0.8)' },
    legText: { flex: 1, color: 'rgba(255,255,255,0.75)', fontSize: 12 },
    legOdds: { color: 'rgba(255,255,255,0.6)', fontSize: 12, fontVariant: ['tabular-nums'] },
    meta: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    metaText: { color: 'rgba(255,255,255,0.5)', fontSize: 12 },
    ev: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 999, backgroundColor: 'rgba(52,211,153,0.12)', borderWidth: 1, borderColor: 'rgba(52,211,153,0.4)' },
    evNeg: { backgroundColor: 'rgba(248,113,113,0.12)', borderColor: 'rgba(248,113,113,0.4)' },
    evText: { color: '#34D399', fontSize: 11, fontWeight: '800', fontVariant: ['tabular-nums'] },
    run: { flexDirection: 'row', alignItems: 'center', gap: 4, marginLeft: 'auto', paddingHorizontal: 9, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(52,211,153,0.35)' },
    runText: { color: '#34D399', fontSize: 11, fontWeight: '700' },
});
